import { Component } from "@theme/component";

/**
 * @typedef {Object} ThinBannerVideoRefs
 * @property {HTMLElement} [poster] - Cover image + play button overlay
 */

/**
 * Click-to-play video for the thin-banner section.
 *
 * Initial state shows the cover image (poster) with a centered play icon.
 * Clicking the poster plays the active (visible) video and reveals its native
 *
 * @extends {Component<ThinBannerVideoRefs>}
 */
class ThinBannerVideo extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    // `play`/`pause`/`ended` don't bubble, so listen during the capture phase.
    this.addEventListener("play", this.#onPlay, { signal, capture: true });
    this.addEventListener("pause", this.#onStop, { signal, capture: true });
    this.addEventListener("ended", this.#onStop, { signal, capture: true });

    if (this.dataset.autoplay !== undefined) {
      this.#tryAutoplay();
    }
  }

  /**
   * Programmatic fallback for the HTML autoplay attribute.
   * Handles edge cases where the attribute doesn't fire (e.g. late DOM
   * insertion in the theme editor). Rejection is silently swallowed since
   * there is no poster overlay to fall back to.
   */
  #tryAutoplay() {
    const video = this.#activeVideo();
    if (video && video.paused) {
      video.play().catch(() => {});
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** Invoked declaratively via `on:click="/play"` on the poster button. */
  play() {
    const video = this.#activeVideo();
    if (!video) return;
    const playback = video.play();
    if (playback && typeof playback.catch === "function") {
      playback.catch(() => {
        // Autoplay/gesture rejection — keep the poster visible so the user can retry.
        this.#onStop();
      });
    }
  }

  /**
   * Returns the currently visible <video> (desktop or mobile), falling back to
   * the first one when none report layout (e.g. in the theme editor).
   *
   * @returns {HTMLVideoElement | null}
   */
  #activeVideo() {
    const videos = /** @type {NodeListOf<HTMLVideoElement>} */ (this.querySelectorAll("video"));
    for (const video of videos) {
      if (video.offsetParent !== null) return video;
    }
    return videos[0] ?? null;
  }

  #onPlay = () => this.#setPlaying(true);

  #onStop = () => this.#setPlaying(false);

  /**
   * @param {boolean} isPlaying
   */
  #setPlaying(isPlaying) {
    const poster = this.refs.poster;
    if (poster instanceof HTMLElement) {
      poster.classList.toggle("tw:hidden", isPlaying);
    }
  }
}

if (!customElements.get("thin-banner-video-component")) {
  customElements.define("thin-banner-video-component", ThinBannerVideo);
}
